import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const toFinite = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const nyTimePartsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const getSessionType = (iso) => {
  if (!iso) return 'after';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return 'after';

  const parts = nyTimePartsFormatter.formatToParts(dt);
  const hourPart = parts.find((p) => p.type === 'hour')?.value;
  const minutePart = parts.find((p) => p.type === 'minute')?.value;
  const hour = Number(hourPart);
  const minute = Number(minutePart);

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return 'after';

  const totalMinutes = hour * 60 + minute;
  const regularOpen = 9 * 60 + 30;
  const regularClose = 16 * 60;

  if (totalMinutes < regularOpen) return 'pre';
  if (totalMinutes < regularClose) return 'market';
  return 'after';
};

const sessionMeta = {
  pre: { label: 'Pre-Market', fill: '#dbeafe', text: '#1d4ed8' },
  market: { label: 'Market Hours', fill: '#dcfce7', text: '#166534' },
  after: { label: 'After-Hours', fill: '#f3f4f6', text: '#374151' },
};

const formatTs = (iso) => {
  if (!iso) return '-';
  try {
    const dt = new Date(iso);
    return dt.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
};

const formatTsLong = (iso) => {
  if (!iso) return '-';
  try {
    const dt = new Date(iso);
    return dt.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
};

const formatCompactNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (Math.abs(n) >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${Math.round(n)}`;
};

const lineColorByKey = {
  entry: '#2563eb',
  stop: '#dc2626',
  target: '#16a34a',
};

const lineLabelByKey = {
  entry: 'Entry',
  stop: 'Stop',
  target: 'Target',
};

const MIN_BODY_HEIGHT_PX = 4;
const MIN_WICK_HEIGHT_PX = 8;
const MIN_WICK_OUTSIDE_BODY_PX = 2;
const MIN_VISIBLE_BARS = 20;
const DEFAULT_VISIBLE_BARS = 140;
const MIN_X_STEP_PX = 5;
const X_ZOOM_FACTOR = 0.78;
const LIVE_REFRESH_MS = 1000;
const VOLUME_PANE_RATIO = 0.22;
const PANE_GAP_PX = 14;

const chartHeight = 560;
const margin = { top: 24, right: 92, bottom: 52, left: 68 };

const scrollBarStyles = `
  .signal-chart-scrollbars {
    scrollbar-width: auto;
    scrollbar-color: #9ca3af #e5e7eb;
  }

  .signal-chart-scrollbars::-webkit-scrollbar {
    height: 12px;
  }

  .signal-chart-scrollbars::-webkit-scrollbar-track {
    background: #e5e7eb;
    border-radius: 9999px;
  }

  .signal-chart-scrollbars::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 9999px;
    border: 2px solid #e5e7eb;
  }

  .signal-chart-scrollbars::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }
`;

const normalizeWsUrl = (backendUrl) => {
  if (!backendUrl) return null;
  if (backendUrl.startsWith('https://')) return backendUrl.replace('https://', 'wss://') + '/api/ws/prices';
  if (backendUrl.startsWith('http://')) return backendUrl.replace('http://', 'ws://') + '/api/ws/prices';
  return null;
};

const toIntervalMs = (interval) => {
  const raw = String(interval || '').trim().toLowerCase();
  if (!raw) return 5 * 60 * 1000;

  const match = raw.match(/^(\d+)(m|h|d|wk|mo)$/);
  if (!match) return 5 * 60 * 1000;

  const value = Number(match[1]);
  const unit = match[2];
  if (!Number.isFinite(value) || value <= 0) return 5 * 60 * 1000;

  if (unit === 'm') return value * 60 * 1000;
  if (unit === 'h') return value * 60 * 60 * 1000;
  if (unit === 'd') return value * 24 * 60 * 60 * 1000;
  if (unit === 'wk') return value * 7 * 24 * 60 * 60 * 1000;
  if (unit === 'mo') return value * 30 * 24 * 60 * 60 * 1000;
  return 5 * 60 * 1000;
};

const isIntradayInterval = (interval) => {
  const raw = String(interval || '').trim().toLowerCase();
  return ['1m', '2m', '5m', '15m', '30m', '60m', '90m', '1h'].includes(raw);
};

const isRegularSessionIso = (iso) => {
  if (!iso) return false;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return false;

  const parts = nyTimePartsFormatter.formatToParts(dt);
  const hourPart = parts.find((p) => p.type === 'hour')?.value;
  const minutePart = parts.find((p) => p.type === 'minute')?.value;
  const hour = Number(hourPart);
  const minute = Number(minutePart);

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return false;
  const totalMinutes = hour * 60 + minute;
  return totalMinutes >= (9 * 60 + 30) && totalMinutes <= (16 * 60);
};

const filterRegularSessionCandles = (candles, interval) => {
  if (!Array.isArray(candles) || candles.length === 0) return [];
  if (!isIntradayInterval(interval)) return candles;
  return candles.filter((candle) => isRegularSessionIso(candle?.time));
};

const updateCandlesWithQuote = (candles, quote, interval) => {
  if (!Array.isArray(candles) || candles.length === 0 || !quote) return candles;

  const latestPrice = toFinite(quote.price);
  if (latestPrice === null) return candles;

  const quoteTsRaw = quote.source_timestamp || quote.timestamp;
  const quoteTs = new Date(quoteTsRaw);
  if (Number.isNaN(quoteTs.getTime())) return candles;

  if (isIntradayInterval(interval) && !isRegularSessionIso(quoteTs.toISOString())) {
    return candles;
  }

  const updated = [...candles]
    .map((c) => ({ ...c }))
    .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

  const intervalMs = toIntervalMs(interval);
  const last = updated[updated.length - 1];
  const lastTs = new Date(last.time);
  if (Number.isNaN(lastTs.getTime())) return candles;

  const liveVolume = toFinite(quote.volume);
  const quoteVolume = liveVolume !== null ? Math.max(0, liveVolume) : null;

  if (quoteTs.getTime() - lastTs.getTime() >= intervalMs * 0.98) {
    const nextOpen = toFinite(last.close) ?? latestPrice;
    updated.push({
      time: quoteTs.toISOString(),
      open: nextOpen,
      high: latestPrice,
      low: latestPrice,
      close: latestPrice,
      volume: quoteVolume,
    });
    return updated;
  }

  last.close = latestPrice;
  const prevHigh = toFinite(last.high);
  const prevLow = toFinite(last.low);
  last.high = prevHigh === null ? latestPrice : Math.max(prevHigh, latestPrice);
  last.low = prevLow === null ? latestPrice : Math.min(prevLow, latestPrice);
  if (quoteVolume !== null) {
    const prevVolume = toFinite(last.volume);
    last.volume = prevVolume === null ? quoteVolume : Math.max(prevVolume, quoteVolume);
  }

  return updated;
};

const isTypingElement = (target) => {
  if (!target) return false;
  const tag = String(target.tagName || '').toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable;
};

export default function SignalChartModal({ scan, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);
  const [liveTick, setLiveTick] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);
  const [chartWidth, setChartWidth] = useState(1200);
  const [zoomBars, setZoomBars] = useState(100);
  const [yZoomScale, setYZoomScale] = useState(1);
  const [panOffsetBars, setPanOffsetBars] = useState(0);
  const [scaleMode, setScaleMode] = useState('fit');
  const [isPanning, setIsPanning] = useState(false);
  const panStateRef = useRef({ startX: 0, startOffset: 0 });
  const [hoveredCandle, setHoveredCandle] = useState(null);
  const chartContainerRef = useRef(null);
  const wsRef = useRef(null);
  const wsReconnectTimerRef = useRef(null);

  const liveSymbol = String(scan?.symbol || '').trim().toUpperCase();

  const totalBars = chartData?.candles?.length || 0;

  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.innerText = scrollBarStyles;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  useEffect(() => {
    if (totalBars > 0) {
      const intervalRaw = String(scan?.interval || chartData?.interval || '').trim().toLowerCase();
      const defaultVisibleBars = intervalRaw === '1m' ? 90 : DEFAULT_VISIBLE_BARS;
      setZoomBars(Math.max(MIN_VISIBLE_BARS, Math.min(totalBars, defaultVisibleBars)));
      setYZoomScale(1);
      setPanOffsetBars(0);
    }
  }, [totalBars, scan?.id, scan?.interval, chartData?.interval]);

  useEffect(() => {
    setLiveTick(0);
  }, [scan?.id]);

  const fetchChart = useCallback(async (showSpinner = false) => {
    if (!scan?.id) return;
    if (showSpinner) {
      setLoading(true);
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/scanner/signal-chart/${scan.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { _ts: Date.now() },
        }
      );
      const raw = response.data;
      const nextInterval = raw?.interval || scan?.interval;
      const nextCandles = filterRegularSessionCandles(raw?.candles || [], nextInterval);
      setChartData({
        ...raw,
        candles: nextCandles,
        point_count: nextCandles.length,
      });
      setError('');
      setLiveTick(Date.now());
    } catch (err) {
      setError((Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || 'Failed to load chart data.');
    } finally {
      if (showSpinner) {
        setLoading(false);
      }
    }
  }, [scan?.id]);

  useEffect(() => {
    if (!scan?.id) return;
    let intervalId = null;
    fetchChart(true);
    intervalId = window.setInterval(() => {
      fetchChart(false);
    }, LIVE_REFRESH_MS);

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [scan?.id, fetchChart]);

  useEffect(() => {
    if (!liveSymbol) return undefined;

    const wsUrl = normalizeWsUrl(BACKEND_URL);
    if (!wsUrl) return undefined;

    let isUnmounted = false;

    const scheduleReconnect = () => {
      if (isUnmounted) return;
      if (wsReconnectTimerRef.current) {
        window.clearTimeout(wsReconnectTimerRef.current);
      }
      wsReconnectTimerRef.current = window.setTimeout(() => {
        connectWs();
      }, 3000);
    };

    const connectWs = () => {
      if (isUnmounted) return;

      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          if (isUnmounted) return;
          setWsConnected(true);
          ws.send(JSON.stringify({ symbols: [liveSymbol] }));
        };

        ws.onmessage = (event) => {
          if (isUnmounted) return;

          try {
            const data = JSON.parse(event.data);

            if (data.type === 'ping') {
              ws.send(JSON.stringify({ type: 'pong' }));
              return;
            }

            const applyQuote = (quote) => {
              if (!quote || quote.symbol !== liveSymbol) return;

              setChartData((prev) => {
                if (!prev || !Array.isArray(prev.candles) || prev.candles.length === 0) return prev;
                const nextCandles = updateCandlesWithQuote(prev.candles, quote, prev.interval || scan?.interval);
                if (nextCandles === prev.candles) return prev;
                return {
                  ...prev,
                  candles: nextCandles,
                  point_count: nextCandles.length,
                  as_of: quote.timestamp || new Date().toISOString(),
                };
              });

              setLiveTick(Date.now());
            };

            if (data.type === 'price_update') {
              applyQuote(data);
              return;
            }

            if (data.type === 'price_batch_update' && Array.isArray(data.quotes)) {
              data.quotes.forEach(applyQuote);
            }
          } catch (_) {
            // Ignore malformed websocket payloads.
          }
        };

        ws.onerror = () => {
          if (isUnmounted) return;
          setWsConnected(false);
        };

        ws.onclose = () => {
          if (isUnmounted) return;
          setWsConnected(false);
          scheduleReconnect();
        };
      } catch (_) {
        setWsConnected(false);
        scheduleReconnect();
      }
    };

    connectWs();

    return () => {
      isUnmounted = true;
      setWsConnected(false);

      if (wsReconnectTimerRef.current) {
        window.clearTimeout(wsReconnectTimerRef.current);
        wsReconnectTimerRef.current = null;
      }

      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch (_) {
          // Ignore cleanup close errors.
        }
        wsRef.current = null;
      }
    };
  }, [liveSymbol, scan?.interval]);

  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el) return;

    const updateSize = () => {
      const nextWidth = Math.max(320, Math.floor(el.clientWidth));
      setChartWidth(nextWidth);
    };

    updateSize();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }

    const observer = new ResizeObserver(updateSize);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const plot = useMemo(() => {
    const safeCandles = filterRegularSessionCandles(chartData?.candles || [], chartData?.interval || scan?.interval);
    const allCandles = [...safeCandles].sort((a, b) => {
      const ta = new Date(a?.time || 0).getTime();
      const tb = new Date(b?.time || 0).getTime();
      return ta - tb;
    });
    if (!allCandles.length) {
      return {
        candles: [],
        overlays: [],
        priceMin: 0,
        priceMax: 1,
        volumeMax: 0,
        totalCandles: 0,
        visibleCandles: 0,
      };
    }

    const visibleCount = Math.min(allCandles.length, Math.max(MIN_VISIBLE_BARS, zoomBars));
    const maxOffset = Math.max(0, allCandles.length - visibleCount);
    const clampedOffset = Math.min(maxOffset, Math.max(0, panOffsetBars));
    const windowEndExclusive = allCandles.length - clampedOffset;
    const windowStart = Math.max(0, windowEndExclusive - visibleCount);
    const visibleWindowCandles = allCandles.slice(windowStart, windowEndExclusive);

    const perfInnerWidth = Math.max(1, chartWidth - margin.left - margin.right);
    const maxRenderCandles = Math.max(100, Math.floor(perfInnerWidth / 3));
    const renderStep = Math.max(1, Math.ceil(visibleWindowCandles.length / maxRenderCandles));
    const candles = renderStep === 1
      ? visibleWindowCandles
      : visibleWindowCandles.filter((_, idx) => idx % renderStep === 0 || idx === visibleWindowCandles.length - 1);

    const candlePrices = [];
    candles.forEach((c) => {
      const high = toFinite(c.high);
      const low = toFinite(c.low);
      if (high !== null) candlePrices.push(high);
      if (low !== null) candlePrices.push(low);
    });

    if (!candlePrices.length) {
      return {
        candles: [],
        overlays: [],
        priceMin: 0,
        priceMax: 1,
        volumeMax: 0,
        totalCandles: allCandles.length,
        visibleCandles: 0,
      };
    }

    const volumeMax = candles.reduce((max, candle) => {
      const volume = toFinite(candle.volume);
      if (volume === null || volume < 0) return max;
      return Math.max(max, volume);
    }, 0);

    const entry = toFinite(chartData?.entry_price);
    const stop = toFinite(chartData?.stop_price);
    const target = toFinite(chartData?.target_price);
    const signalPrices = [entry, stop, target].filter((price) => price !== null);

    const candleMin = Math.min(...candlePrices);
    const candleMax = Math.max(...candlePrices);
    const candleRange = Math.max(candleMax - candleMin, 0.01);
    // Use price-relative minimum padding so low-priced stocks aren't over-stretched.
    const midPrice = (candleMin + candleMax) / 2;
    const padding = Math.max(candleRange * 0.15, midPrice * 0.003);

    let min = candleMin - padding;
    let max = candleMax + padding;

    if (scaleMode === 'include_set' && signalPrices.length > 0) {
      min = Math.min(min, Math.min(...signalPrices) - padding);
      max = Math.max(max, Math.max(...signalPrices) + padding);
    } else if (scaleMode === 'auto' && signalPrices.length > 0) {
      const signalMin = Math.min(...signalPrices);
      const signalMax = Math.max(...signalPrices);
      const signalBelow = Math.max(0, min - signalMin);
      const signalAbove = Math.max(0, signalMax - max);
      const blend = 0.38;

      min -= signalBelow * blend;
      max += signalAbove * blend;

      // Preserve a minimum visible range so candles don't get flattened.
      const blendedRange = Math.max(max - min, candleRange * 1.9);
      const centeredMin = candleMin - blendedRange * 0.45;
      const centeredMax = candleMax + blendedRange * 0.45;
      min = Math.min(min, centeredMin);
      max = Math.max(max, centeredMax);
    }

    const overlays = [
      { key: 'entry', value: entry },
      { key: 'stop', value: stop },
      { key: 'target', value: target },
    ].filter((o) => o.value !== null);

    const center = (min + max) / 2;
    const halfRange = ((max - min) / 2) * yZoomScale;
    min = center - halfRange;
    max = center + halfRange;

    return {
      candles,
      overlays,
      priceMin: min - padding * 0.5,
      priceMax: max + padding * 0.5,
      volumeMax,
      totalCandles: allCandles.length,
      visibleCandles: visibleWindowCandles.length,
      renderedCandles: candles.length,
      clampedOffset,
    };
  }, [chartData, zoomBars, yZoomScale, panOffsetBars, scaleMode, chartWidth]);

  const handleZoomIn = () => {
    setZoomBars((prev) => Math.max(MIN_VISIBLE_BARS, Math.floor(prev * X_ZOOM_FACTOR)));
  };

  const handleZoomOut = () => {
    setZoomBars((prev) => {
      const next = Math.ceil(prev / X_ZOOM_FACTOR);
      return totalBars > 0 ? Math.min(totalBars, next) : next;
    });
  };

  const handleZoomReset = () => {
    if (totalBars > 0) {
      setZoomBars(totalBars);
    }
    setYZoomScale(1);
    setPanOffsetBars(0);
  };

  const handleChartWheel = (event) => {
    event.preventDefault();
    const zoomIn = event.deltaY < 0;

    if (event.shiftKey) {
      setYZoomScale((prev) => {
        const next = zoomIn ? prev * 0.88 : prev * 1.12;
        return Math.max(0.35, Math.min(3, next));
      });
      return;
    }

    if (!totalBars) return;

    const container = chartContainerRef.current;
    const rect = container?.getBoundingClientRect();
    const cursorX = rect ? event.clientX - rect.left : 0;
    const scrollLeft = container?.scrollLeft || 0;
    const cursorPlotX = scrollLeft + cursorX;
    const relative = Math.max(0, Math.min(1, (cursorPlotX - margin.left) / plotInnerWidth));

    const oldVisible = Math.min(totalBars, Math.max(MIN_VISIBLE_BARS, zoomBars));
    const newVisible = zoomIn
      ? Math.max(MIN_VISIBLE_BARS, Math.floor(oldVisible * X_ZOOM_FACTOR))
      : Math.min(totalBars, Math.ceil(oldVisible / X_ZOOM_FACTOR));

    const maxOldOffset = Math.max(0, totalBars - oldVisible);
    const oldOffset = Math.min(maxOldOffset, Math.max(0, panOffsetBars));
    const oldStart = totalBars - oldOffset - oldVisible;
    const focusIndex = oldStart + Math.floor(relative * Math.max(1, oldVisible - 1));

    const newStartUnclamped = focusIndex - Math.floor(relative * Math.max(1, newVisible - 1));
    const maxNewStart = Math.max(0, totalBars - newVisible);
    const newStart = Math.max(0, Math.min(maxNewStart, newStartUnclamped));
    const newOffset = totalBars - (newStart + newVisible);

    setZoomBars(newVisible);
    setPanOffsetBars(Math.max(0, newOffset));
  };

  const handlePanStart = (event) => {
    if (!plot.totalCandles || plot.visibleCandles >= plot.totalCandles) return;
    setIsPanning(true);
    panStateRef.current = {
      startX: event.clientX,
      startOffset: plot.clampedOffset || 0,
    };
  };

  const handlePanMove = (event) => {
    if (!isPanning) return;
    const deltaX = event.clientX - panStateRef.current.startX;
    const barsPerPixel = plot.visibleCandles / Math.max(1, plotInnerWidth);
    const deltaBars = Math.round(-deltaX * barsPerPixel);
    const maxOffset = Math.max(0, plot.totalCandles - plot.visibleCandles);
    const nextOffset = Math.max(0, Math.min(maxOffset, panStateRef.current.startOffset + deltaBars));
    setPanOffsetBars(nextOffset);
  };

  const handlePanEnd = () => {
    setIsPanning(false);
  };

  const handlePanBars = (direction) => {
    const step = Math.max(5, Math.floor(plot.visibleCandles * 0.2));
    const maxOffset = Math.max(0, plot.totalCandles - plot.visibleCandles);
    setPanOffsetBars((prev) => {
      const next = prev + direction * step;
      return Math.max(0, Math.min(maxOffset, next));
    });
  };

  const handleYZoomIn = () => {
    setYZoomScale((prev) => Math.max(0.35, prev * 0.88));
  };

  const handleYZoomOut = () => {
    setYZoomScale((prev) => Math.min(3, prev * 1.12));
  };

  const handleYZoomReset = () => {
    setYZoomScale(1);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isTypingElement(event.target)) return;

      const key = event.key;
      const lower = String(key || '').toLowerCase();

      if (key === 'Escape') {
        event.preventDefault();
        onClose?.();
        return;
      }

      if (key === '+' || key === '=') {
        event.preventDefault();
        handleZoomIn();
        return;
      }

      if (key === '-' || key === '_') {
        event.preventDefault();
        handleZoomOut();
        return;
      }

      if (key === 'ArrowLeft') {
        event.preventDefault();
        handlePanBars(-1);
        return;
      }

      if (key === 'ArrowRight') {
        event.preventDefault();
        handlePanBars(1);
        return;
      }

      if (event.altKey && key === 'ArrowUp') {
        event.preventDefault();
        handleYZoomIn();
        return;
      }

      if (event.altKey && key === 'ArrowDown') {
        event.preventDefault();
        handleYZoomOut();
        return;
      }

      if (key === '0') {
        event.preventDefault();
        handleZoomReset();
        return;
      }

      if (lower === 'a') {
        event.preventDefault();
        setScaleMode('auto');
        return;
      }

      if (lower === 'f') {
        event.preventDefault();
        setScaleMode('fit');
        return;
      }

      if (lower === 'i') {
        event.preventDefault();
        setScaleMode('include_set');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, handleZoomIn, handleZoomOut, handlePanBars, handleYZoomIn, handleYZoomOut, handleZoomReset]);

  const viewportInnerWidth = Math.max(1, chartWidth - margin.left - margin.right);
  const plotInnerWidth = Math.max(
    viewportInnerWidth,
    plot.candles.length > 1 ? (plot.candles.length - 1) * MIN_X_STEP_PX : viewportInnerWidth
  );
  const svgWidth = margin.left + plotInnerWidth + margin.right;
  const innerHeight = Math.max(1, chartHeight - margin.top - margin.bottom);
  const volumePaneHeight = Math.max(72, Math.floor(innerHeight * VOLUME_PANE_RATIO));
  const pricePaneHeight = Math.max(120, innerHeight - volumePaneHeight - PANE_GAP_PX);
  const volumePaneTop = margin.top + pricePaneHeight + PANE_GAP_PX;
  const volumePaneBottom = volumePaneTop + volumePaneHeight;

  const xStep = plot.candles.length > 1 ? plotInnerWidth / (plot.candles.length - 1) : plotInnerWidth;
  const bodyWidth = Math.max(3, Math.min(16, xStep * 0.72));
  const volumeBarWidth = Math.max(2, Math.min(14, bodyWidth * 0.9));

  const yScale = (price) => {
    if (plot.priceMax === plot.priceMin) return margin.top + innerHeight / 2;
    const ratio = (price - plot.priceMin) / (plot.priceMax - plot.priceMin);
    return margin.top + pricePaneHeight - ratio * pricePaneHeight;
  };

  const clampY = (y) => Math.min(margin.top + pricePaneHeight, Math.max(margin.top, y));
  const volumeScale = (value) => {
    const volume = Math.max(0, value || 0);
    if (!plot.volumeMax) return volumePaneBottom;
    const ratio = Math.min(1, volume / plot.volumeMax);
    return volumePaneBottom - ratio * volumePaneHeight;
  };

  const yTicks = 6;
  const gridLines = Array.from({ length: yTicks }, (_, i) => {
    const ratio = i / (yTicks - 1);
    const value = plot.priceMax - ratio * (plot.priceMax - plot.priceMin);
    return {
        y: margin.top + ratio * pricePaneHeight,
      value,
    };
  });

  const xLabelStep = Math.max(1, Math.floor(plot.candles.length / 6));
  const setCalculatedIso = scan?.scan_timestamp || scan?.status_datetime || scan?.created_at || null;

  const setMarker = useMemo(() => {
    if (!setCalculatedIso || !plot.candles.length) return null;

    const targetTs = new Date(setCalculatedIso).getTime();
    if (Number.isNaN(targetTs)) return null;

    let bestIdx = -1;
    let bestDiff = Number.POSITIVE_INFINITY;

    for (let i = 0; i < plot.candles.length; i += 1) {
      const candleTs = new Date(plot.candles[i]?.time).getTime();
      if (Number.isNaN(candleTs)) continue;
      const diff = Math.abs(candleTs - targetTs);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestIdx = i;
      }
    }

    if (bestIdx < 0) return null;

    return {
      index: bestIdx,
      x: margin.left + bestIdx * xStep,
      time: plot.candles[bestIdx]?.time,
    };
  }, [setCalculatedIso, plot.candles, xStep]);

  // Find the entry candle: first candle at/after retest_time whose close is closest to entry_price.
  const entryMarker = useMemo(() => {
    const ep = toFinite(chartData?.entry_price);
    if (!plot.candles.length || ep === null) return null;

    const retestRaw = scan?.retest_time;
    let retestTs = null;
    if (retestRaw) {
      const d = new Date(String(retestRaw).replace(' ', 'T') + ':00');
      if (!Number.isNaN(d.getTime())) retestTs = d.getTime();
    }

    let bestIdx = -1;
    let bestDiff = Number.POSITIVE_INFINITY;

    for (let i = 0; i < plot.candles.length; i += 1) {
      const c = plot.candles[i];
      const close = toFinite(c?.close);
      if (close === null) continue;
      if (retestTs !== null) {
        const candleTs = new Date(c.time).getTime();
        if (!Number.isNaN(candleTs) && candleTs < retestTs) continue;
      }
      const diff = Math.abs(close - ep);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestIdx = i;
      }
    }

    if (bestIdx < 0) return null;
    return {
      index: bestIdx,
      x: margin.left + bestIdx * xStep,
      candle: plot.candles[bestIdx],
    };
  }, [chartData?.entry_price, plot.candles, scan?.retest_time, xStep]);

  const latestCandle = plot.candles.length ? plot.candles[plot.candles.length - 1] : null;
  const latestPrice = toFinite(latestCandle?.close);
  const latestVolume = toFinite(latestCandle?.volume);

  const entryPrice = toFinite(chartData?.entry_price);
  const stopPrice = toFinite(chartData?.stop_price);
  const targetPrice = toFinite(chartData?.target_price);

  const distanceFromEntryPct = latestPrice !== null && entryPrice !== null && entryPrice !== 0
    ? ((latestPrice - entryPrice) / entryPrice) * 100
    : null;
  const distanceToTargetPct = latestPrice !== null && targetPrice !== null && latestPrice !== 0
    ? ((targetPrice - latestPrice) / latestPrice) * 100
    : null;
  const distanceToStopPct = latestPrice !== null && stopPrice !== null && latestPrice !== 0
    ? ((latestPrice - stopPrice) / latestPrice) * 100
    : null;

  const formatPct = (value) => {
    if (!Number.isFinite(value)) return '-';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const sessionSpans = useMemo(() => {
    if (!plot.candles.length) return [];

    const spans = [];
    let current = getSessionType(plot.candles[0]?.time);
    let startIdx = 0;

    for (let i = 1; i < plot.candles.length; i += 1) {
      const nextSession = getSessionType(plot.candles[i]?.time);
      if (nextSession !== current) {
        spans.push({ session: current, start: startIdx, end: i - 1 });
        current = nextSession;
        startIdx = i;
      }
    }

    spans.push({ session: current, start: startIdx, end: plot.candles.length - 1 });
    return spans;
  }, [plot.candles]);

  const panMaxBars = Math.max(0, plot.totalCandles - plot.visibleCandles);
  const sliderValue = Math.max(0, Math.min(panMaxBars, panMaxBars - Math.round(panOffsetBars)));

  const handleScrollBarChange = (event) => {
    const next = Number(event.target.value);
    if (!Number.isFinite(next)) return;
    const clamped = Math.max(0, Math.min(panMaxBars, Math.round(next)));
    const mappedOffset = panMaxBars - clamped;
    setPanOffsetBars(mappedOffset);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 sm:p-6">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[96rem] max-h-[95vh] overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900">
              {scan.symbol} Signal Candles ({chartData?.interval || scan.interval || '-'})
            </h2>
            <p className="text-xs sm:text-sm text-gray-600">
              Scan #{scan.id} • {scan.signal_type || 'N/A'} • Source: {chartData?.source || 'yahoo_finance'}
            </p>
            <p className="text-xs sm:text-sm text-gray-600">
              Scanner period: {scan.period || '-'} • Chart period: {chartData?.period || '-'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 text-2xl leading-none"
            aria-label="Close chart modal"
          >
            ×
          </button>
        </div>

        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(95vh-82px)] space-y-4">
          {loading ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="bg-red-100 text-red-800 px-4 py-3 rounded">{error}</div>
          ) : !plot.candles.length ? (
            <div className="bg-amber-100 text-amber-800 px-4 py-3 rounded">No candle data returned from Yahoo Finance.</div>
          ) : (
            <>
              <div
                ref={chartContainerRef}
                onWheel={handleChartWheel}
                onMouseDown={handlePanStart}
                onMouseMove={handlePanMove}
                onMouseUp={handlePanEnd}
                onMouseLeave={handlePanEnd}
                className={`signal-chart-scrollbars w-full overflow-x-scroll overflow-y-hidden border border-gray-200 rounded-lg bg-white ${isPanning ? 'cursor-grabbing' : 'cursor-grab'}`}
                style={{ WebkitOverflowScrolling: 'touch', scrollbarGutter: 'stable both-edges' }}
              >
                <svg width={svgWidth} height={chartHeight} role="img" aria-label="Signal candlestick chart">
                  {sessionSpans.map((span, idx) => {
                    const meta = sessionMeta[span.session] || sessionMeta.after;

                    const startX = margin.left + span.start * xStep - xStep / 2;
                    const endX = margin.left + span.end * xStep + xStep / 2;
                    const safeStartX = Math.max(margin.left, startX);
                    const safeEndX = Math.min(margin.left + plotInnerWidth, endX);
                    const width = Math.max(0, safeEndX - safeStartX);
                    if (width <= 0) return null;

                    return (
                      <g key={`session-${idx}-${span.session}`}>
                        <rect
                          x={safeStartX}
                          y={margin.top}
                          width={width}
                          height={pricePaneHeight}
                          fill={meta.fill}
                          opacity="0.2"
                        />
                        {width > 72 ? (
                          <text
                            x={safeStartX + 6}
                            y={margin.top + 12}
                            fontSize="10"
                            fill={meta.text}
                            fontWeight="700"
                          >
                            {meta.label}
                          </text>
                        ) : null}

                        {span.session === 'market' ? (
                          <>
                            <line
                              x1={safeStartX}
                              y1={margin.top}
                              x2={safeStartX}
                              y2={margin.top + pricePaneHeight}
                              stroke="#1d4ed8"
                              strokeWidth="1.2"
                              strokeDasharray="4 4"
                              opacity="0.9"
                            />
                            <line
                              x1={safeEndX}
                              y1={margin.top}
                              x2={safeEndX}
                              y2={margin.top + pricePaneHeight}
                              stroke="#1d4ed8"
                              strokeWidth="1.2"
                              strokeDasharray="4 4"
                              opacity="0.9"
                            />
                          </>
                        ) : null}
                      </g>
                    );
                  })}

                  {gridLines.map((line, idx) => (
                    <g key={`grid-${idx}`}>
                      <line
                        x1={margin.left}
                        y1={line.y}
                        x2={margin.left + plotInnerWidth}
                        y2={line.y}
                        stroke="#e5e7eb"
                        strokeDasharray="3 3"
                      />
                      <text x={8} y={line.y + 4} fontSize="11" fill="#6b7280">
                        {line.value.toFixed(2)}
                      </text>
                    </g>
                  ))}

                  {plot.overlays.map((overlay) => {
                    const rawY = yScale(overlay.value);
                    const y = clampY(rawY);
                    const isOffScale = rawY !== y;
                    const labelText = `${lineLabelByKey[overlay.key]} ${overlay.value.toFixed(2)}${isOffScale ? ' (off-scale)' : ''}`;
                    return (
                      <g key={overlay.key}>
                        <line
                          x1={margin.left}
                          y1={y}
                          x2={margin.left + plotInnerWidth}
                          y2={y}
                          stroke={lineColorByKey[overlay.key]}
                          strokeWidth="1.3"
                          strokeDasharray="6 4"
                        />
                        <text
                          x={margin.left + plotInnerWidth + 8}
                          y={y + 2.5}
                          fontSize="10.5"
                          fill={lineColorByKey[overlay.key]}
                          fontWeight="700"
                        >
                          {labelText}
                        </text>
                      </g>
                    );
                  })}

                  {setMarker ? (
                    <g>
                      <line
                        x1={setMarker.x}
                        y1={margin.top}
                        x2={setMarker.x}
                        y2={volumePaneBottom}
                        stroke="#7c3aed"
                        strokeWidth="1.4"
                        strokeDasharray="5 4"
                        opacity="0.95"
                      />
                      <text
                        x={Math.min(setMarker.x + 6, margin.left + plotInnerWidth - 130)}
                        y={margin.top + 12}
                        fontSize="10"
                        fill="#6d28d9"
                        fontWeight="700"
                      >
                        Scan
                      </text>
                    </g>
                  ) : null}

                  <line
                    x1={margin.left}
                    y1={volumePaneTop}
                    x2={margin.left + plotInnerWidth}
                    y2={volumePaneTop}
                    stroke="#e5e7eb"
                  />

                  {plot.candles.map((candle, idx) => {
                    const x = margin.left + idx * xStep;
                    const open = toFinite(candle.open);
                    const close = toFinite(candle.close);
                    const volume = Math.max(0, toFinite(candle.volume) || 0);
                    if (open === null || close === null) return null;

                    const up = close >= open;
                    const y = volumeScale(volume);

                    return (
                      <rect
                        key={`v-${idx}`}
                        x={x - volumeBarWidth / 2}
                        y={y}
                        width={volumeBarWidth}
                        height={Math.max(1, volumePaneBottom - y)}
                        fill={up ? '#34d399' : '#f87171'}
                        opacity="0.75"
                      />
                    );
                  })}

                  <text x={8} y={volumePaneTop + 12} fontSize="11" fill="#6b7280">Vol</text>
                  <text x={8} y={volumePaneBottom - 4} fontSize="11" fill="#6b7280">
                    {plot.volumeMax ? Math.round(plot.volumeMax).toLocaleString() : '0'}
                  </text>

                  {plot.candles.map((candle, idx) => {
                    const x = margin.left + idx * xStep;
                    const open = toFinite(candle.open);
                    const close = toFinite(candle.close);
                    const high = toFinite(candle.high);
                    const low = toFinite(candle.low);
                    if ([open, close, high, low].some((v) => v === null)) return null;

                    const up = close >= open;
                    const color = up ? '#16a34a' : '#dc2626';
                    const yOpen = clampY(yScale(open));
                    const yClose = clampY(yScale(close));
                    const bodyMid = (yOpen + yClose) / 2;
                    const rawBodyHeight = Math.abs(yClose - yOpen);
                    const bodyHeight = Math.max(MIN_BODY_HEIGHT_PX, rawBodyHeight);
                    const bodyTop = bodyMid - bodyHeight / 2;
                    const bodyBottom = bodyTop + bodyHeight;

                    let yHigh = clampY(yScale(high));
                    let yLow = clampY(yScale(low));
                    if (Math.abs(yLow - yHigh) < MIN_WICK_HEIGHT_PX) {
                      yHigh = bodyMid - MIN_WICK_HEIGHT_PX / 2;
                      yLow = bodyMid + MIN_WICK_HEIGHT_PX / 2;
                    }

                    // Ensure visible wick outside the body for ultra-tight candles.
                    yHigh = Math.min(yHigh, bodyTop - MIN_WICK_OUTSIDE_BODY_PX);
                    yLow = Math.max(yLow, bodyBottom + MIN_WICK_OUTSIDE_BODY_PX);
                    yHigh = clampY(yHigh);
                    yLow = clampY(yLow);

                    if (yLow <= yHigh) {
                      const half = Math.max(MIN_WICK_HEIGHT_PX / 2, MIN_WICK_OUTSIDE_BODY_PX + 1);
                      yHigh = clampY(bodyMid - half);
                      yLow = clampY(bodyMid + half);
                    }

                    return (
                      <g
                        key={`c-${idx}`}
                        className="cursor-pointer"
                        onMouseEnter={() => {
                          setHoveredCandle({
                            x,
                            y: bodyTop,
                            open,
                            close,
                            high,
                            low,
                            volume: Math.max(0, toFinite(candle.volume) || 0),
                            time: candle.time,
                          });
                        }}
                        onMouseLeave={() => setHoveredCandle(null)}
                      >
                        <rect
                          x={x - bodyWidth / 2}
                          y={bodyTop}
                          width={bodyWidth}
                          height={bodyHeight}
                          fill={up ? '#bbf7d0' : '#fecaca'}
                          stroke={color}
                          strokeWidth="1"
                        />
                        <line x1={x} y1={yHigh} x2={x} y2={yLow} stroke={color} strokeWidth="2" />
                      </g>
                    );
                  })}

                  {/* Entry point marker icon */}
                  {entryMarker && (() => {
                    const ec = entryMarker.candle;
                    const high = toFinite(ec?.high);
                    const low  = toFinite(ec?.low);
                    const mid  = toFinite(ec?.close) ?? toFinite(ec?.open);
                    const isCall = (scan?.signal_type || chartData?.signal_type) !== 'PUT';
                    const ex = entryMarker.x;

                    if (isCall) {
                      const baseY = clampY(yScale(high ?? mid ?? 0)) - 6;
                      const tipY  = baseY + 10;
                      return (
                        <g key="entry-marker" pointerEvents="none">
                          <polygon
                            points={`${ex - 7},${baseY} ${ex + 7},${baseY} ${ex},${tipY}`}
                            fill="#2563eb"
                            opacity="0.92"
                          />
                          <text x={ex} y={baseY - 3} textAnchor="middle" fontSize="9" fill="#2563eb" fontWeight="700">
                            Entry
                          </text>
                        </g>
                      );
                    }

                    // PUT — upward triangle below candle
                    const baseY = clampY(yScale(low ?? mid ?? 0)) + 6;
                    const tipY  = baseY - 10;
                    return (
                      <g key="entry-marker" pointerEvents="none">
                        <polygon
                          points={`${ex - 7},${baseY} ${ex + 7},${baseY} ${ex},${tipY}`}
                          fill="#2563eb"
                          opacity="0.92"
                        />
                        <text x={ex} y={baseY + 12} textAnchor="middle" fontSize="9" fill="#2563eb" fontWeight="700">
                          Entry
                        </text>
                      </g>
                    );
                  })()}

                  {hoveredCandle && (
                    <g pointerEvents="none">
                      <line
                        x1={hoveredCandle.x}
                        y1={margin.top}
                        x2={hoveredCandle.x}
                        y2={volumePaneBottom}
                        stroke="#9ca3af"
                        strokeDasharray="4 4"
                      />
                      {(() => {
                        const tooltipWidth = 232;
                        const tooltipHeight = 116;
                        const preferredX = hoveredCandle.x + 14;
                        const leftBound = margin.left + 4;
                        const rightBound = margin.left + plotInnerWidth - tooltipWidth - 4;
                        const tooltipX = Math.max(leftBound, Math.min(rightBound, preferredX));
                        const tooltipY = margin.top + 8;

                        return (
                          <g>
                            <rect
                              x={tooltipX}
                              y={tooltipY}
                              width={tooltipWidth}
                              height={tooltipHeight}
                              rx="8"
                              ry="8"
                              fill="#111827"
                              opacity="0.94"
                            />
                            <text x={tooltipX + 10} y={tooltipY + 20} fontSize="12" fill="#f9fafb" fontWeight="600">
                              {formatTsLong(hoveredCandle.time)}
                            </text>
                            <text x={tooltipX + 10} y={tooltipY + 42} fontSize="12" fill="#93c5fd">
                              Open: {hoveredCandle.open.toFixed(2)}
                            </text>
                            <text x={tooltipX + 10} y={tooltipY + 58} fontSize="12" fill="#86efac">
                              Close: {hoveredCandle.close.toFixed(2)}
                            </text>
                            <text x={tooltipX + 10} y={tooltipY + 74} fontSize="12" fill="#fca5a5">
                              High/Low: {hoveredCandle.high.toFixed(2)} / {hoveredCandle.low.toFixed(2)}
                            </text>
                            <text x={tooltipX + 10} y={tooltipY + 90} fontSize="12" fill="#fcd34d">
                              Volume: {Math.round(hoveredCandle.volume).toLocaleString()}
                            </text>
                          </g>
                        );
                      })()}
                    </g>
                  )}

                  {plot.candles.map((candle, idx) => {
                    if (idx % xLabelStep !== 0 && idx !== plot.candles.length - 1) return null;
                    const x = margin.left + idx * xStep;
                    return (
                      <text key={`x-${idx}`} x={x} y={chartHeight - 14} textAnchor="middle" fontSize="10" fill="#6b7280">
                        {formatTs(candle.time)}
                      </text>
                    );
                  })}
                </svg>
              </div>

              <div className="px-1 pt-2">
                <input
                  type="range"
                  min={0}
                  max={panMaxBars}
                  step={1}
                  value={sliderValue}
                  onChange={handleScrollBarChange}
                  disabled={panMaxBars <= 0}
                  className="w-full accent-blue-600"
                  aria-label="Chart horizontal scroll"
                />
              </div>

              <p className="text-xs text-gray-500">
                Candles: {chartData?.point_count || 0} • Visible: {plot.visibleCandles} • Rendered: {plot.renderedCandles || plot.visibleCandles} • Requested period: {chartData?.requested_period || scan.period || '-'} • Effective period: {chartData?.period || '-'} • Interval: {chartData?.interval || '-'} • Volume pane: live (1s poll + WebSocket)
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
